/* eslint-disable import/prefer-default-export */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { t } from 'i18next';
import { Box } from '@mui/system';
import useUserRole from '../../../hooks/useUserRoles';
import useNotificationFeedStore from '../../../store/zustand/notificationFeed';
import { ROLES } from '../../../helpers/roles';
import api from '../../../api';
import useNotificationsStore from '../../../store/zustand/notifications';

const REFRESH_INTERVAL = 60000; // 60 seconds in milliseconds
const ERROR_COOLDOWN = 300000; // 5 minutes cooldown after error

const NotificationIndicator: React.FC = function NotificationIndicator() {
  const navigate = useNavigate();
  const { addNotification } = useNotificationsStore((state) => state);
  const { setUnreadCount, unreadCount } = useNotificationFeedStore();

  const intervalRef = useRef<any>(null);
  const errorTimerRef = useRef<any>(null);
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const canReceiveNotifications = useUserRole([
    ROLES.OPERATIONS,
    ROLES.BUSINESS_DEVELOPMENT,
    ROLES.SALES
  ]);

  // Reset error state after cooldown
  const handleError = useCallback(() => {
    setHasError(true);
    setShouldFetch(false);

    if (errorTimerRef.current) {
      clearTimeout(intervalRef.current);
    }

    if (intervalRef.current) {
      clearTimeout(errorTimerRef.current);
    }

    errorTimerRef.current = setTimeout(() => {
      setHasError(false);
    }, ERROR_COOLDOWN);
  }, []);

  // Fetch notifications data
  const { data, error, mutate, isValidating } = useSWR(
    shouldFetch ? { url: 'getTotalUnreadNotifications' } : null,
    api.NotificationFeed.getTotalUnreadNotifications,
    { onError: handleError }
  );

  // Handle successful data fetch
  useEffect(() => {
    if (data) {
      setShouldFetch(false);
      setUnreadCount(data.data.totalUnread);
      mutate(undefined, false);
    }
  }, [data, setUnreadCount]);

  // Handle error notifications
  useEffect(() => {
    if (error) {
      setShouldFetch(false);
      mutate(undefined, false);
    }
  }, [error]);

  // Set up polling interval
  useEffect(() => {
    if (!canReceiveNotifications) return;

    // Set up polling
    intervalRef.current = setInterval(() => {
      if (!isValidating && !hasError) {
        setShouldFetch(true);
      }
    }, REFRESH_INTERVAL);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(errorTimerRef.current);
    };
  }, [canReceiveNotifications, hasError]);

  // Trigger initial fetch
  useEffect(() => {
    if (canReceiveNotifications) {
      setShouldFetch(true);
    }
  }, []);

  return (
    <Box
      onClick={() => navigate('/app/notifications')}
      sx={{
        alignItems: 'center',
        cursor: 'pointer',
        display: 'flex',
        height: '40px',
        justifyContent: 'center',
        position: 'fixed',
        right: '300px',
        width: '40px'
      }}
    >
      {unreadCount > 0 && (
        <Box
          sx={{
            backgroundColor: '#FF4D00',
            borderRadius: '50%',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 700,
            height: '24px',
            lineHeight: '24px',
            position: 'absolute',
            right: '-8px',
            textAlign: 'center',
            top: '-8px',
            width: '24px'
          }}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </Box>
      )}
      <NotificationsNoneIcon sx={{ color: '#7D8090' }} />
    </Box>
  );
};

export default NotificationIndicator;
